import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { X, CheckCircle } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import { GAME_TYPES, TEAM_GAMES } from '../../utils/constants';

const GameSetupModal = ({ event, onClose }) => {
  const { currentUser, users, setActiveGame, setView, showAlert } = useContext(AppContext);
  const [step, setStep] = useState(1);
  const [gameType, setGameType] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([currentUser.id]);
  const [teams, setTeams] = useState([{ id: 'A', name: 'Team A', playerIds: [] }, { id: 'B', name: 'Team B', playerIds: [] }]);
  const [courseName, setCourseName] = useState(event.course || '');
  const [totalHoles, setTotalHoles] = useState(18);
  const [defaultPar, setDefaultPar] = useState(4);
  const [creating, setCreating] = useState(false);

  const attendeeUsers = event.attendees?.map(id => users.find(u => u.id === id)).filter(Boolean) || [];
  const isTeamGame = TEAM_GAMES.includes(gameType);

  const handleStartGame = async () => {
    if (!gameType || selectedPlayers.length < 2) return;
    setCreating(true);
    try {
      const holes = {};
      for (let i = 1; i <= totalHoles; i++) {
        holes[String(i)] = { par: defaultPar, scores: {} };
      }

      const players = selectedPlayers.map(id => {
        const u = users.find(usr => usr.id === id);
        return { userId: id, name: u?.name || 'Unknown' };
      });

      const gameData = {
        eventId: event.id,
        gameType,
        courseName: SecurityUtils.sanitizeText(courseName),
        players,
        teams: isTeamGame ? teams.map(t => ({ ...t, playerIds: t.playerIds.filter(id => selectedPlayers.includes(id)) })) : [],
        holes,
        totalHoles,
        createdBy: currentUser.id,
      };

      // Add game-type specific data
      if (gameType === 'wolf') {
        gameData.wolfOrder = selectedPlayers;
      }
      if (gameType === 'skins') {
        gameData.skins = {};
        for (let i = 1; i <= totalHoles; i++) gameData.skins[String(i)] = null;
      }
      if (gameType === 'nassau') {
        gameData.nassauScores = { front: {}, back: {}, overall: {} };
      }
      if (gameType === 'press') {
        gameData.presses = [];
      }

      const created = await firestoreService.createGame(gameData);
      setActiveGame({ id: created.id, ...gameData, status: 'active', currentHole: 1, startedAt: new Date().toISOString() });
      setView('live-score');
      onClose();
    } catch (err) {
      console.error('Error creating game:', err);
      showAlert('Failed to start game.', 'Error');
    }
    setCreating(false);
  };

  const togglePlayer = (userId) => {
    setSelectedPlayers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const assignToTeam = (playerId, teamId) => {
    setTeams(prev => prev.map(t => ({
      ...t,
      playerIds: t.id === teamId
        ? (t.playerIds.includes(playerId) ? t.playerIds.filter(id => id !== playerId) : [...t.playerIds, playerId])
        : t.playerIds.filter(id => id !== playerId)
    })));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">Start Game {step > 1 && `(Step ${step})`}</h3>
          <button onClick={onClose} className="text-white hover:text-gray-300"><X className="w-6 h-6" /></button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-gray-400 text-sm mb-2">Select a game type</p>
            {GAME_TYPES.map(gt => (
              <button key={gt.id} onClick={() => { setGameType(gt.id); setStep(2); }} className={`w-full p-3 rounded-lg border text-left transition-colors ${gameType === gt.id ? 'border-[#16a34a] bg-[#16a34a] bg-opacity-10' : 'border-gray-700 hover:border-gray-500'}`}>
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{gt.emoji}</span>
                  <div>
                    <p className="text-white font-bold">{gt.name}</p>
                    <p className="text-gray-400 text-sm">{gt.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Select players from event attendees</p>
            <div className="space-y-2">
              {attendeeUsers.map(u => (
                <button key={u.id} onClick={() => togglePlayer(u.id)} className={`flex items-center justify-between w-full p-2 rounded-lg transition-colors ${selectedPlayers.includes(u.id) ? 'bg-[#16a34a] bg-opacity-20 border border-[#16a34a]' : 'hover:bg-gray-900 border border-transparent'}`}>
                  <div className="flex items-center space-x-3">
                    <img src={u.profilePicture} alt={u.name} className="w-10 h-10 rounded-full" />
                    <span className="text-white font-medium">{u.name}</span>
                  </div>
                  {selectedPlayers.includes(u.id) && <CheckCircle className="w-5 h-5 text-[#16a34a]" />}
                </button>
              ))}
            </div>
            <div className="flex space-x-2">
              <button onClick={() => setStep(1)} className="flex-1 py-2 bg-gray-700 text-white rounded-lg">Back</button>
              <button onClick={() => setStep(isTeamGame ? 3 : 4)} disabled={selectedPlayers.length < 2} className="flex-1 py-2 bg-[#16a34a] text-pure-white rounded-lg disabled:opacity-50">Next</button>
            </div>
          </div>
        )}

        {step === 3 && isTeamGame && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">Assign players to teams</p>
            {teams.map(team => (
              <div key={team.id} className="border border-gray-700 rounded-lg p-3">
                <h4 className="text-white font-bold mb-2">{team.name}</h4>
                <div className="space-y-1">
                  {selectedPlayers.map(pid => {
                    const u = users.find(usr => usr.id === pid);
                    if (!u) return null;
                    return (
                      <button key={pid} onClick={() => assignToTeam(pid, team.id)} className={`flex items-center space-x-2 w-full p-1 rounded text-sm ${team.playerIds.includes(pid) ? 'bg-[#16a34a] bg-opacity-20 text-[#16a34a]' : 'text-gray-400 hover:text-white'}`}>
                        <span>{team.playerIds.includes(pid) ? '✓' : '○'}</span>
                        <span>{u.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex space-x-2">
              <button onClick={() => setStep(2)} className="flex-1 py-2 bg-gray-700 text-white rounded-lg">Back</button>
              <button onClick={() => setStep(4)} className="flex-1 py-2 bg-[#16a34a] text-pure-white rounded-lg">Next</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">Course Name</label>
              <input type="text" value={courseName} onChange={e => setCourseName(e.target.value)} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black focus:ring-2 focus:ring-[#16a34a]" placeholder="Course name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Total Holes</label>
                <select value={totalHoles} onChange={e => setTotalHoles(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black">
                  <option value={9}>9 Holes</option>
                  <option value={18}>18 Holes</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Default Par</label>
                <select value={defaultPar} onChange={e => setDefaultPar(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-700 rounded-lg text-white bg-black">
                  <option value={3}>Par 3</option>
                  <option value={4}>Par 4</option>
                  <option value={5}>Par 5</option>
                </select>
              </div>
            </div>
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-white text-sm font-medium mb-1">Game: {GAME_TYPES.find(g => g.id === gameType)?.name}</p>
              <p className="text-gray-400 text-sm">Players: {selectedPlayers.length} | Holes: {totalHoles}</p>
            </div>
            <div className="flex space-x-2">
              <button onClick={() => setStep(isTeamGame ? 3 : 2)} className="flex-1 py-2 bg-gray-700 text-white rounded-lg">Back</button>
              <button onClick={handleStartGame} disabled={creating || !courseName.trim()} className="flex-1 py-2 bg-[#16a34a] text-pure-white rounded-lg disabled:opacity-50">
                {creating ? 'Starting...' : 'Start Game'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameSetupModal;
